import { useState } from "react";
import clsx from "clsx";
import Tooltip from "@mui/material/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleExclamation, faSpinner } from "@fortawesome/free-solid-svg-icons";

type VerificationIndicatorProps = {
  isVerified: boolean;
  /** Grouped rows only: how many of the group's rows are checked, and how many there are. */
  checkedCount?: number;
  totalCount?: number;
  /** Omit for a read-only marker. Provided, the indicator becomes the accept control. */
  onAccept?: () => Promise<unknown> | void;
  /** What accepting means on this screen, e.g. "load". Used in the tooltip. */
  subject?: string;
  disabled?: boolean;
  className?: string;
};

/**
 * The one place Eclipse says "a machine recorded this and nobody has checked it".
 *
 * Note: an icon rather than a badge plus a button. Every screen showing this had a "Needs checking"
 * pill stacked over a full-width green Accept button, which is three stacked elements in a narrow
 * column and reads as clutter once a shift has more than a handful of rows - and telemetry writes one
 * row per haul, so it always does. One glyph carries the same two states.
 *
 * Note: the amber alert swaps to a green tick on HOVER, before anything is clicked. That is the
 * affordance doing the explaining - it shows what the click will produce, so the control needs no label.
 * The swap is two stacked icons and group-hover rather than React state, so it costs no re-render.
 *
 * Note: verified renders as a plain tick with no button, deliberately. There is no un-accept: clearing
 * the flag again is not a thing the API offers, and a control that looks clickable but is not is worse
 * than no control.
 */
export const VerificationIndicator: React.FC<VerificationIndicatorProps> = ({
  isVerified,
  checkedCount,
  totalCount,
  onAccept,
  subject = "row",
  disabled = false,
  className,
}) => {
  const [isWorking, setIsWorking] = useState(false);

  // Note: A THIRD STATE, and it is the normal one for a group on a live shift. Inference inserts one row
  // per haul and never revisits a row it already wrote, so accepting a group at 10:00 does not stop forty
  // more rows landing by 18:00 - the group is then part checked, and a green tick would be a claim that
  // went stale minutes after it was made. Showing the counts says exactly how far the checking got, and
  // reverts to amber on its own as new rows arrive. A group only reads fully checked once nothing is
  // still adding to it, which in practice means once the shift has ended.
  const isPartiallyChecked =
    totalCount != null && checkedCount != null && checkedCount > 0 && checkedCount < totalCount;

  if (isPartiallyChecked) {
    const outstanding = totalCount! - checkedCount!;
    const marker = (
      <span className="inline-flex items-center gap-1">
        <FontAwesomeIcon icon={faCircleExclamation} className="text-theme-warning text-lg" />
        <span className="text-theme-text-muted text-xs whitespace-nowrap">
          {checkedCount}/{totalCount}
        </span>
      </span>
    );

    return (
      <Tooltip
        title={`${checkedCount} of ${totalCount} checked, ${outstanding} still to check`}
        arrow
        placement="top"
      >
        {onAccept ? (
          <button
            type="button"
            onClick={async () => {
              if (isWorking || disabled) return;
              setIsWorking(true);
              try { await onAccept(); } finally { setIsWorking(false); }
            }}
            disabled={isWorking || disabled}
            aria-label={`Accept the remaining ${outstanding} of ${totalCount}`}
            className={clsx(
              "inline-flex items-center cursor-pointer transition hover:opacity-80",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              className
            )}
          >
            {isWorking
              ? <FontAwesomeIcon icon={faSpinner} spin className="text-theme-warning text-lg" />
              : marker}
          </button>
        ) : (
          <span className={clsx("inline-flex items-center", className)}>{marker}</span>
        )}
      </Tooltip>
    );
  }

  if (isVerified) {
    return (
      <Tooltip title="Checked" arrow placement="top">
        <span className={clsx("inline-flex items-center", className)}>
          <FontAwesomeIcon icon={faCircleCheck} className="text-theme-success text-lg" />
        </span>
      </Tooltip>
    );
  }

  if (!onAccept) {
    return (
      <Tooltip title="Recorded by telemetry, not checked by anyone yet" arrow placement="top">
        <span className={clsx("inline-flex items-center", className)}>
          <FontAwesomeIcon icon={faCircleExclamation} className="text-theme-warning text-lg" />
        </span>
      </Tooltip>
    );
  }

  const handleClick = async () => {
    if (isWorking || disabled) return;

    setIsWorking(true);
    try {
      await onAccept();
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Tooltip
      title={disabled ? "Needs checking" : `Needs checking. Accept this ${subject} as recorded`}
      arrow
      placement="top"
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={isWorking || disabled}
        aria-label={`Accept this ${subject} as recorded`}
        className={clsx(
          "group relative inline-flex items-center justify-center cursor-pointer transition",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          className
        )}
      >
        {isWorking ? (
          <FontAwesomeIcon icon={faSpinner} spin className="text-theme-warning text-lg" />
        ) : (
          <>
            <FontAwesomeIcon
              icon={faCircleExclamation}
              className="text-theme-warning text-lg transition group-hover:opacity-0"
            />
            <FontAwesomeIcon
              icon={faCircleCheck}
              className="absolute text-theme-success text-lg opacity-0 transition group-hover:opacity-100 group-hover:scale-110"
            />
          </>
        )}
      </button>
    </Tooltip>
  );
};

export default VerificationIndicator;
